"use client";

import dynamic from "next/dynamic";
import { useState, useEffect } from "react";
import { m, AnimatePresence } from "framer-motion";
import { LaunchConfig } from "@/lib/config/launch-config";
import type { Countdown as CountdownData } from "@/lib/validations/cms";

const LaunchControl = dynamic(() => import("@/components/home/LaunchControl"), {
  ssr: false,
});

interface LaunchGateProps {
  config: LaunchConfig;
  countdown: CountdownData;
  children: React.ReactNode;
  isAdmin?: boolean;
}

export default function LaunchGate({ config, countdown, children, isAdmin = false }: LaunchGateProps) {
  const initiallyLocked = config.mode !== "open" && !isAdmin;
  const [isLocked, setIsLocked] = useState(initiallyLocked);

  useEffect(() => {
    setIsLocked(config.mode !== "open" && !isAdmin);
  }, [config.mode, isAdmin]);

  useEffect(() => {
    if (isLocked) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => document.body.classList.remove("overflow-hidden");
  }, [isLocked]);

  const handleUnlock = () => {
    setIsLocked(false);
  };

  return (
    <>
      <AnimatePresence mode="wait">
        {isLocked && (
          <LaunchControl config={config} countdown={countdown} onUnlock={handleUnlock} />
        )}
      </AnimatePresence>

      <m.div
        initial={initiallyLocked ? { opacity: 0 } : false}
        animate={{ opacity: isLocked ? 0 : 1 }}
        transition={{ duration: 1.0, delay: isLocked ? 0 : 0.5 }}
        className="flex min-h-screen flex-1 flex-col"
        style={{ pointerEvents: isLocked ? "none" : "auto" }}
        aria-hidden={isLocked}
      >
        {children}
      </m.div>
    </>
  );
}
